<template>
  <v-app
    class="booking-app"
    :class="{ 'embedded': isEmbedded }"
    :style="paletteStyle"
  >
    <v-container
      fluid
      class="booking-engine pa-0"
      :class="{ 'embedded': isEmbedded }"
    >
      <!-- Header (oculto em modo embedded/iframe) -->
      <v-app-bar
        v-if="!isEmbedded"
        class="booking-app-bar"
        color="transparent"
        dark
        flat
        height="70"
      >
        <v-container class="d-flex align-center justify-space-between py-0">
          <div class="d-flex align-center">
            <div
              v-if="hotelInfo.logo"
              class="mr-3"
            >
              <v-img
                :src="hotelInfo.logo"
                max-height="50"
                max-width="100"
                contain
              ></v-img>
            </div>
            <div>
              <div class="header-title">
                {{ hotelInfo.name }}
              </div>
              <div class="header-subtitle">
                Sistema de Reservas Online
              </div>
            </div>
          </div>
          <div class="header-contact d-none d-md-flex">
            <v-chip
              v-if="hotelInfo.phone"
              small
              outlined
              color="white"
              text-color="white"
              class="header-chip mr-2"
            >
              <v-icon
                left
                small
              >
                mdi-phone
              </v-icon>
              {{ hotelInfo.phone }}
            </v-chip>
            <v-chip
              v-if="hotelInfo.email"
              small
              outlined
              color="white"
              text-color="white"
              class="header-chip"
            >
              <v-icon
                left
                small
              >
                mdi-email
              </v-icon>
              {{ hotelInfo.email }}
            </v-chip>
          </div>
        </v-container>
      </v-app-bar>

      <!-- Conteúdo Principal -->
      <div class="main-content">
        <!-- Motor Desabilitado -->
        <v-container
          v-if="!bookingEnabled"
          class="text-center py-12"
        >
          <v-icon
            size="64"
            color="warning"
          >
            mdi-alert-circle-outline
          </v-icon>
          <h2 class="mt-4">
            Motor de Reservas Desabilitado
          </h2>
          <p class="text--secondary mt-2">
            O sistema de reservas não está disponível no momento.
          </p>
        </v-container>

        <!-- Motor Habilitado -->
        <v-container
          v-else
          class="pa-0"
        >
          <v-container
            v-if="isCorporateFlow && !isCorporateAuthenticated"
            class="py-12"
          >
            <v-row justify="center">
              <v-col
                cols="12"
                md="6"
                lg="5"
              >
                <v-card
                  class="pa-4 pa-md-6"
                  elevation="8"
                >
                  <div class="text-overline mb-2">
                    Acesso corporativo
                  </div>
                  <h2 class="mb-2">
                    Login do hóspede corporativo
                  </h2>
                  <p class="text--secondary mb-6">
                    Entre com o e-mail corporativo cadastrado para acessar as tarifas e condições da sua empresa.
                  </p>
                  <v-text-field
                    v-model="corporateCredentials.email"
                    label="E-mail corporativo"
                    outlined
                    dense
                    class="mb-2"
                  />
                  <v-text-field
                    v-model="corporateCredentials.password"
                    label="Senha"
                    type="password"
                    outlined
                    dense
                    class="mb-2"
                    @keyup.enter="loginCorporate"
                  />
                  <v-btn
                    color="primary"
                    block
                    :loading="corporateLoading"
                    @click="loginCorporate"
                  >
                    Entrar
                  </v-btn>
                  <v-btn
                    text
                    block
                    class="mt-2"
                    :loading="corporateLoading"
                    @click="forgotCorporatePassword"
                  >
                    Esqueci minha senha
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>
          </v-container>

          <section
            v-else
            class="hero-section"
          >
            <span class="hero-shape hero-shape--one"></span>
            <span class="hero-shape hero-shape--two"></span>
            <v-container class="py-10">
              <v-row align="center">
                <v-col
                  cols="12"
                  md="7"
                >
                  <p class="hero-kicker">
                    {{ isCorporateFlow ? 'Seu canal de reservas corporativas' : 'Experiência Chess Hotel' }}
                  </p>
                  <h1 class="hero-title">
                    {{ isCorporateFlow ? 'Seu canal de reservas corporativas' : 'Reserve sua estadia com conforto e praticidade' }}
                  </h1>
                  <p class="hero-subtitle">
                    {{ isCorporateFlow
                      ? 'Planeje viagens da equipe com tarifas negociadas, processo ágil e pagamento conforme as regras da empresa.'
                      : 'Descubra tarifas exclusivas, benefícios e um atendimento pensado para surpreender. ' + hotelInfo.name + ' garante o melhor preço direto no site.' }}
                  </p>
                  <div
                    v-if="isCorporateFlow && isCorporateAuthenticated"
                    class="corporate-session-info mb-4"
                  >
                    <v-chip
                      color="white"
                      outlined
                      class="mr-2 mb-2 transparent-chip"
                    >
                      <v-icon
                        left
                        small
                      >
                        {{ icons.mdiOfficeBuilding }}
                      </v-icon>
                      {{ corporateCompanyDisplayName }}
                    </v-chip>
                    <v-btn
                      small
                      outlined
                      color="white"
                      class="mb-2"
                      :loading="corporateLoading"
                      @click="logoutCorporate"
                    >
                      <v-icon
                        left
                        small
                      >
                        {{ icons.mdiLogout }}
                      </v-icon>
                      Sair
                    </v-btn>
                  </div>
                  <div class="hero-chips">
                    <v-chip
                      v-for="(highlight, index) in highlights"
                      :key="`highlight-${index}`"
                      outlined
                      color="white"
                      class="mr-2 mb-2 transparent-chip"
                    >
                      <v-icon
                        left
                        small
                      >
                        {{ highlight.icon }}
                      </v-icon>
                      {{ highlight.text }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  md="5"
                >
                  <v-card
                    class="summary-card"
                    elevation="12"
                  >
                    <v-card-text class="summary-card__body">
                      <div class="summary-card__header summary-card__header--search">
                        <div class="summary-card__title">
                          <span class="summary-eyebrow">Buscar</span>
                          <span class="summary-title">Disponibilidade</span>
                          <span class="summary-subtitle">
                            {{ isCorporateFlow ? 'Selecione datas e hóspedes para ver tarifas corporativas.' : 'Selecione datas e hóspedes para ver tarifas.' }}
                          </span>
                        </div>
                        <v-avatar
                          size="44"
                          class="hero-avatar"
                          tile
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              :d="summaryIcon"
                              fill="currentColor"
                            />
                          </svg>
                        </v-avatar>
                      </div>

                      <div class="summary-form">
                        <v-row dense>
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-menu
                              v-model="checkInMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="290px"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  :value="searchParams.check_in | formatDate"
                                  label="Check-in"
                                  prepend-inner-icon="mdi-calendar"
                                  readonly
                                  v-bind="attrs"
                                  filled
                                  rounded
                                  dense
                                  hide-details
                                  class="summary-input summary-input--active"
                                  v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="searchParams.check_in"
                                :min="minCheckIn"
                                locale="pt-BR"
                                first-day-of-week="0"
                                @input="checkInMenu = false"
                              ></v-date-picker>
                            </v-menu>
                          </v-col>

                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-menu
                              v-model="checkOutMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="290px"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  :value="searchParams.check_out | formatDate"
                                  label="Check-out"
                                  prepend-inner-icon="mdi-calendar"
                                  readonly
                                  v-bind="attrs"
                                  filled
                                  rounded
                                  dense
                                  hide-details
                                  class="summary-input summary-input--active"
                                  v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="searchParams.check_out"
                                :min="minCheckOut"
                                locale="pt-BR"
                                first-day-of-week="0"
                                @input="checkOutMenu = false"
                              ></v-date-picker>
                            </v-menu>
                          </v-col>

                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-text-field
                              v-model.number="searchParams.adults"
                              label="Adultos"
                              type="number"
                              min="1"
                              filled
                              rounded
                              dense
                              hide-details
                              class="summary-input summary-input--active"
                            ></v-text-field>
                          </v-col>

                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-text-field
                              v-model.number="searchParams.children"
                              label="Crianças"
                              type="number"
                              min="0"
                              filled
                              rounded
                              dense
                              hide-details
                              class="summary-input summary-input--active"
                            ></v-text-field>
                          </v-col>

                          <v-col cols="12">
                            <v-btn
                              class="search-action-btn"
                              block
                              large
                              :loading="loading"
                              @click="searchAvailability"
                            >
                              <v-icon left>
                                mdi-magnify
                              </v-icon>
                              {{ isCorporateFlow ? 'Buscar disponibilidade corporativa' : 'Buscar agora' }}
                            </v-btn>
                          </v-col>
                        </v-row>
                      </div>

                      <div class="summary-compact-note">
                        Resultados atualizados instantaneamente.
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </section>

          <v-container class="engine-container pb-12">
            <div class="engine-background"></div>
            <v-card
              class="engine-card glass-card"
              elevation="8"
            >
              <v-stepper
                v-model="step"
                elevation="0"
                flat
                class="booking-stepper"
              >
                <v-stepper-header>
                  <v-stepper-step
                    :complete="step > 1"
                    step="1"
                  >
                    Selecionar quarto
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step
                    :complete="step > 2"
                    step="2"
                  >
                    Dados do Hóspede
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step
                    :complete="step > 3"
                    step="3"
                  >
                    Pagamento
                  </v-stepper-step>
                </v-stepper-header>

                <v-stepper-items>
                  <!-- Passo 1: Selecionar quarto -->
                  <v-stepper-content step="1">
                    <!-- Resultados -->
                    <v-row
                      v-if="loading"
                      class="mb-4"
                    >
                      <v-col
                        v-for="n in 3"
                        :key="`skeleton-${n}`"
                        cols="12"
                        md="6"
                        lg="4"
                      >
                        <v-skeleton-loader
                          type="card"
                          class="room-card-skeleton"
                        ></v-skeleton-loader>
                      </v-col>
                    </v-row>

                    <v-slide-y-transition>
                      <div v-if="availableRooms.length > 0">
                        <v-row>
                          <v-col
                            v-for="category in availableRooms"
                            :key="category.id"
                            cols="12"
                            md="6"
                          >
                            <v-card
                              elevation="3"
                              class="room-card room-card--list"
                            >
                              <div class="room-card__top">
                                <div class="room-card__media">
                                  <v-img
                                    v-if="category.cover_image"
                                    :src="category.cover_image.url"
                                    height="140"
                                    width="190"
                                    class="room-card__image"
                                  >
                                    <template v-slot:placeholder>
                                      <v-row
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                      >
                                        <v-progress-circular
                                          indeterminate
                                          color="grey lighten-3"
                                        ></v-progress-circular>
                                      </v-row>
                                    </template>
                                  </v-img>
                                  <div
                                    v-else
                                    class="room-card__no-image"
                                  >
                                    <v-icon
                                      size="48"
                                      color="grey lighten-1"
                                    >
                                      mdi-image-off
                                    </v-icon>
                                  </div>
                                </div>

                                <div class="room-card__content">
                                  <div class="room-card__title">
                                    {{ category.name }}
                                  </div>
                                  <div class="room-card__stats">
                                    <div
                                      v-if="formatOccupancy(category)"
                                      class="room-card__stat"
                                    >
                                      <v-icon
                                        small
                                        class="mr-1"
                                      >
                                        mdi-account-group
                                      </v-icon>
                                      <span>Ocup. máx.: {{ formatOccupancy(category) }}</span>
                                    </div>
                                    <div
                                      v-if="category.size"
                                      class="room-card__stat"
                                    >
                                      <v-icon
                                        small
                                        class="mr-1"
                                      >
                                        mdi-ruler-square
                                      </v-icon>
                                      <span>Área: {{ category.size }}</span>
                                    </div>
                                    <div
                                      v-if="category.bed_type"
                                      class="room-card__stat"
                                    >
                                      <v-icon
                                        small
                                        class="mr-1"
                                      >
                                        mdi-bed
                                      </v-icon>
                                      <span>{{ category.bed_type }}</span>
                                    </div>
                                  </div>
                                  <div class="room-card__amenities">
                                    <div
                                      v-for="line in amenityLines(category)"
                                      :key="`amenity-line-${category.id}-${line.key}`"
                                      class="amenity-line"
                                    >
                                      <div class="amenity-line__items">
                                        <v-chip
                                          v-for="amenity in line.items"
                                          :key="`amenity-${category.id}-${line.key}-${amenity.id}`"
                                          x-small
                                          outlined
                                          class="mr-1 mb-1"
                                        >
                                          <v-icon
                                            v-if="resolveMdiIcon(amenity.icon)"
                                            x-small
                                            left
                                          >
                                            {{ resolveMdiIcon(amenity.icon) }}
                                          </v-icon>
                                          {{ amenity.name }}
                                        </v-chip>
                                        <v-chip
                                          v-if="line.moreCount > 0"
                                          x-small
                                          color="primary"
                                          text-color="white"
                                          class="mr-1 mb-1"
                                        >
                                          +{{ line.moreCount }} mais
                                        </v-chip>
                                      </div>
                                    </div>
                                  </div>
                                  <v-btn
                                    text
                                    small
                                    class="mt-1 px-0 room-card__details-btn"
                                    @click="openCategoryDetails(category)"
                                  >
                                    Ver detalhe do quarto
                                    <v-icon
                                      right
                                      small
                                    >
                                      {{ icons.mdiChevronRight }}
                                    </v-icon>
                                  </v-btn>
                                </div>
                              </div>

                              <v-divider></v-divider>

                              <div class="room-card__bottom">
                                <div class="room-card__grid">
                                  <div class="room-card__col">
                                    <div class="room-card__col-title">
                                      Condições de Estadia
                                    </div>
                                    <div class="room-card__badges">
                                      <v-chip
                                        v-for="method in availablePaymentMethods"
                                        :key="`method-${category.id}-${method.value}`"
                                        x-small
                                        outlined
                                        class="mr-1 mb-1"
                                      >
                                        {{ method.label }}
                                      </v-chip>
                                    </div>
                                    <div class="room-card__conditions">
                                      <div
                                        v-if="category.require_payment"
                                        class="room-card__condition-line"
                                      >
                                        <v-icon
                                          x-small
                                          class="mr-1"
                                        >
                                          {{ icons.mdiCreditCardOutline }}
                                        </v-icon>
                                        <span>
                                          {{
                                            category.no_show_guarantee
                                              ? 'Garantia No-Show (1 diária)'
                                              : 'Pagamento antecipado'
                                          }}
                                        </span>
                                      </div>
                                      <div
                                        v-else
                                        class="room-card__condition-line"
                                      >
                                        <v-icon
                                          x-small
                                          class="mr-1"
                                        >
                                          {{ icons.mdiCash }}
                                        </v-icon>
                                        <span>Pagamento no hotel</span>
                                      </div>
                                      <div
                                        v-if="category.require_payment"
                                        class="room-card__condition-amount"
                                      >
                                        {{ formatCurrency(category.advance_payment) }}
                                      </div>
                                      <div class="room-card__condition-line">
                                        <v-icon
                                          x-small
                                          class="mr-1"
                                          :color="cancellationPolicyColor()"
                                        >
                                          {{ cancellationPolicyIcon() }}
                                        </v-icon>
                                        <span>{{ cancellationPolicyLabel() }}</span>
                                      </div>
                                      <div
                                        v-if="categoryPolicyAmenities(category).length"
                                        class="room-card__policy-tags"
                                      >
                                        <v-chip
                                          v-for="amenity in categoryPolicyAmenities(category)"
                                          :key="`policy-${category.id}-${amenity.id}`"
                                          x-small
                                          outlined
                                          class="mr-1 mb-1"
                                        >
                                          <v-icon
                                            v-if="resolveMdiIcon(amenity.icon)"
                                            x-small
                                            left
                                          >
                                            {{ resolveMdiIcon(amenity.icon) }}
                                          </v-icon>
                                          {{ amenity.name }}
                                        </v-chip>
                                      </div>
                                    </div>
                                  </div>

                                  <div class="room-card__col">
                                    <div class="room-card__col-title">
                                      Hóspedes
                                    </div>
                                    <div class="guest-icons">
                                      <v-icon
                                        v-for="n in adultIconCount(category)"
                                        :key="`adult-${category.id}-${n}`"
                                        small
                                      >
                                        {{ icons.mdiAccount }}
                                      </v-icon>
                                      <v-icon
                                        v-for="n in childIconCount(category)"
                                        :key="`child-${category.id}-${n}`"
                                        small
                                      >
                                        {{ icons.mdiAccountChild }}
                                      </v-icon>
                                    </div>
                                  </div>

                                  <div class="room-card__col">
                                    <div class="room-card__col-title">
                                      Preço para {{ totalGuestsLabel }}
                                    </div>
                                    <div class="room-card__price">
                                      {{ formatCurrency(category.base_price) }} / noite
                                    </div>
                                    <div class="room-card__price-total">
                                      Total de {{ formatCurrency(category.total_price) }}
                                      para {{ category.days }} {{ category.days === 1 ? 'noite' : 'noites' }}
                                    </div>
                                    <div class="room-card__price-note">
                                      Impostos e taxas não inclusos
                                    </div>
                                  </div>

                                  <div class="room-card__col room-card__col--action">
                                    <div class="room-card__col-title">
                                      Nº de quartos
                                    </div>
                                    <div class="room-card__rooms">
                                      {{ category.available_rooms }}
                                    </div>
                                    <v-btn
                                      color="primary"
                                      dark
                                      class="room-card__action"
                                      @click="selectRoom(category)"
                                    >
                                      Escolher
                                    </v-btn>
                                  </div>
                                </div>
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-slide-y-transition>

                    <v-alert
                      v-if="searched && !loading && availableRooms.length === 0"
                      type="info"
                      outlined
                      class="mt-6"
                    >
                      Nenhum quarto disponível para as datas selecionadas.
                    </v-alert>
                  </v-stepper-content>

                  <!-- Passo 2: Dados do Hóspede -->
                  <v-stepper-content step="2">
                    <v-row>
                      <v-col
                        cols="12"
                        md="8"
                      >
                        <v-card
                          class="mb-4 section-card glass-card"
                          elevation="0"
                        >
                          <v-card-title>Dados do Hóspede</v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col
                                cols="12"
                                md="6"
                              >
                                <v-text-field
                                  v-model="guestData.name"
                                  label="Nome Completo *"
                                  outlined
                                  dense
                                  :rules="[rules.required]"
                                ></v-text-field>
                              </v-col>

                              <v-col
                                cols="12"
                                md="6"
                              >
                                <v-text-field
                                  v-model="guestData.cpf"
                                  v-mask="'###.###.###-##'"
                                  label="CPF *"
                                  outlined
                                  dense
                                  :rules="[rules.cpf]"
                                ></v-text-field>
                              </v-col>

                              <v-col
                                cols="12"
                                md="6"
                              >
                                <v-text-field
                                  v-model="guestData.email"
                                  label="E-mail *"
                                  type="email"
                                  outlined
                                  dense
                                  :rules="[rules.required, rules.email]"
                                ></v-text-field>
                              </v-col>

                              <v-col
                                cols="12"
                                md="6"
                              >
                                <v-text-field
                                  v-model="guestData.phone"
                                  v-mask="'(##) #####-####'"
                                  label="Telefone *"
                                  outlined
                                  dense
                                  :rules="[rules.required]"
                                ></v-text-field>
                              </v-col>

                              <v-col
                                v-if="childAges.length > 0"
                                cols="12"
                              >
                                <div class="guest-extra-section">
                                  <div class="guest-extra-section__title">
                                    Idade da{{ childAges.length > 1 ? 's' : '' }} criança{{ childAges.length > 1 ? 's' : '' }}
                                  </div>
                                  <v-row dense>
                                    <v-col
                                      v-for="(age, index) in childAges"
                                      :key="`child-age-${index}`"
                                      cols="12"
                                      sm="6"
                                      md="4"
                                    >
                                      <v-text-field
                                        v-model.number="childAges[index]"
                                        :label="childAges.length === 1 ? 'Idade *' : `Criança ${index + 1} *`"
                                        type="number"
                                        min="0"
                                        max="17"
                                        outlined
                                        dense
                                        hide-details="auto"
                                        :rules="[rules.childAge]"
                                      ></v-text-field>
                                    </v-col>
                                  </v-row>
                                </div>
                              </v-col>

                              <v-col cols="12">
                                <v-textarea
                                  v-model="specialRequests"
                                  label="Observações e solicitações especiais"
                                  hint="Ex.: andar alto, berço, cama extra, chegada após 22h"
                                  persistent-hint
                                  outlined
                                  rows="4"
                                ></v-textarea>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>

                        <div class="action-row">
                          <v-btn
                            class="back-btn"
                            color="secondary"
                            dark
                            @click="step = 1"
                          >
                            <v-icon left>
                              {{ icons.mdiArrowLeft }}
                            </v-icon>
                            Voltar
                          </v-btn>
                          <v-btn
                            color="primary"
                            dark
                            :disabled="!isGuestDataValid"
                            @click="goToCheckoutStep"
                          >
                            Continuar
                            <v-icon right>
                              {{ icons.mdiArrowRight }}
                            </v-icon>
                          </v-btn>
                        </div>
                      </v-col>

                      <v-col
                        cols="12"
                        md="4"
                      >
                        <BookingSummaryCard
                          :selected-category="selectedCategory"
                          :search-params="searchParams"
                          :payment-method="paymentMethod"
                          :selected-installments="selectedInstallments"
                          :installment-value="installmentValue"
                          :hold-active="holdActive"
                          :hold-countdown-label="holdCountdownLabel"
                          :hold-expired="holdExpired"
                          :hold-token="holdToken"
                        >
                          <template v-slot:footer>
                            <v-btn
                              text
                              block
                              class="room-card__swap-btn"
                              color="primary"
                              @click="step = 1"
                            >
                              <v-icon
                                left
                                small
                              >
                                mdi-arrow-left
                              </v-icon>
                              Trocar quarto
                            </v-btn>
                          </template>
                        </BookingSummaryCard>
                      </v-col>
                    </v-row>
                  </v-stepper-content>

                  <!-- Passo 3: Pagamento -->
                  <v-stepper-content step="3">
                    <v-row>
                      <!-- Coluna Esquerda: Formulário de Pagamento -->
                      <v-col
                        cols="12"
                        md="8"
                      >
                        <v-card
                          class="mb-4 section-card glass-card"
                          elevation="0"
                        >
                          <v-card-title>Forma de Pagamento</v-card-title>
                          <v-card-text>
                            <v-radio-group v-model="paymentMethod">
                              <v-radio
                                v-for="method in availablePaymentMethods"
                                :key="method.value"
                                :label="method.label"
                                :value="method.value"
                              ></v-radio>
                            </v-radio-group>

                            <v-expand-transition>
                              <div
                                v-if="paymentMethod === 'faturamento'"
                                class="mt-3"
                              >
                                <v-card
                                  outlined
                                  class="mb-3"
                                >
                                  <v-card-text>
                                    <div v-if="corporateShowBillingBalance">
                                      <div class="text-subtitle-2 mb-2">
                                        Saldo de faturamento corporativo
                                      </div>
                                      <div class="d-flex flex-wrap align-center mb-2">
                                        <span
                                          class="caption grey--text mr-2"
                                          style="white-space:nowrap"
                                        >
                                          {{ corporateBillingConsumedLabel }} / {{ corporateBillingLimitLabel }}
                                        </span>
                                        <v-progress-linear
                                          :value="corporateCreditUsagePercent"
                                          :color="corporateCreditBarColor"
                                          height="10"
                                          rounded
                                          class="flex-grow-1"
                                        ></v-progress-linear>
                                        <span
                                          class="caption ml-2"
                                          :class="`${corporateCreditBarColor}--text`"
                                        >
                                          {{ corporateCreditUsagePercent }}%
                                        </span>
                                      </div>
                                      <div class="caption">
                                        <strong>Disponível:</strong> {{ corporateBillingAvailableLabel }}
                                      </div>
                                    </div>
                                    <v-alert
                                      v-if="corporateBilling && corporateBilling.credit_blocked"
                                      type="error"
                                      dense
                                      outlined
                                      class="mt-2 mb-0"
                                    >
                                      <strong>Faturamento bloqueado</strong>
                                      <span v-if="corporateBilling && corporateBilling.overdue_days > 0">
                                        — empresa possui faturas vencidas há {{ corporateBilling.overdue_days }} dia{{ corporateBilling.overdue_days !== 1 ? 's' : '' }}
                                        <span v-if="corporateBilling && corporateBilling.billing_overdue_tolerance !== null">(tolerância: {{ corporateBilling.billing_overdue_tolerance }} dias)</span>.
                                      </span>
                                    </v-alert>
                                  </v-card-text>
                                </v-card>

                                <v-radio-group
                                  v-model="billingCheckoutScope"
                                  :mandatory="true"
                                >
                                  <v-radio
                                    label="Faturar somente hospedagem"
                                    value="accommodation"
                                  ></v-radio>
                                  <v-radio
                                    label="Faturar conta total (hospedagem + consumo)"
                                    value="total"
                                  ></v-radio>
                                </v-radio-group>
                              </div>
                            </v-expand-transition>

                            <!-- Informação sobre Checkout Hospedado para Cartão -->
                            <v-expand-transition>
                              <div
                                v-if="paymentMethod === 'cartao' || paymentMethod === 'debito'"
                                class="mt-4"
                              >
                                <v-divider class="mb-4"></v-divider>

                                <!-- Seleção de Parcelas - APENAS para Cartão de Crédito -->
                                <v-card
                                  v-if="paymentMethod === 'cartao'"
                                  outlined
                                  class="mb-4"
                                  color="blue lighten-5"
                                >
                                  <v-card-text>
                                    <div class="d-flex align-center mb-3">
                                      <v-icon
                                        color="primary"
                                        size="32"
                                        class="mr-2"
                                      >
                                        mdi-credit-card-multiple-outline
                                      </v-icon>
                                      <div>
                                        <h3 class="mb-0 primary--text">
                                          Escolha o número de parcelas
                                        </h3>
                                      </div>
                                    </div>

                                    <v-select
                                      v-model="selectedInstallments"
                                      :items="availableInstallments"
                                      label="Selecione a quantidade de parcelas"
                                      outlined
                                      dense
                                      prepend-inner-icon="mdi-calendar-range"
                                      color="primary"
                                      item-color="primary"
                                      hide-details
                                    >
                                      <template v-slot:selection="{ item }">
                                        <strong class="primary--text">{{ item.text }}</strong>
                                      </template>
                                    </v-select>

                                    <v-alert
                                      v-if="selectedInstallments"
                                      color="success"
                                      text
                                      dense
                                      class="mt-3 mb-0"
                                    >
                                      <div class="d-flex align-center">
                                        <v-icon
                                          small
                                          left
                                        >
                                          mdi-check-circle
                                        </v-icon>
                                        <span>
                                          Você pagará <strong>{{ selectedInstallments }}x de {{ formatCurrency(selectedCategory.total_price / selectedInstallments) }}</strong>
                                        </span>
                                      </div>
                                    </v-alert>
                                  </v-card-text>
                                </v-card>

                                <v-alert
                                  type="info"
                                  outlined
                                  class="mb-3"
                                >
                                  <div class="d-flex align-center">
                                    <v-icon
                                      left
                                      color="primary"
                                      size="32"
                                    >
                                      mdi-shield-lock
                                    </v-icon>
                                    <div class="ml-3">
                                      <h3 class="mb-2">
                                        Pagamento 100% Seguro
                                      </h3>
                                      <p class="mb-2">
                                        Ao confirmar a reserva, você será direcionado para uma
                                        <strong>página segura de pagamento</strong> onde poderá inserir os dados do seu cartão com total segurança.
                                      </p>
                                      <p class="mb-0">
                                        <v-icon
                                          small
                                          color="success"
                                        >
                                          mdi-check-circle
                                        </v-icon>
                                        <strong>Você terá {{ paymentHoldMinutes }} minutos</strong> para concluir o pagamento
                                      </p>
                                    </div>
                                  </div>
                                </v-alert>

                                <v-alert
                                  type="success"
                                  outlined
                                  dense
                                >
                                  <v-icon
                                    left
                                    small
                                  >
                                    mdi-security
                                  </v-icon>
                                  <small>
                                    <strong>Total segurança e privacidade:</strong> Seus dados de cartão são digitados diretamente
                                    no ambiente seguro do nosso parceiro de pagamentos. Nós nunca teremos acesso a essas informações.
                                  </small>
                                </v-alert>
                              </div>
                            </v-expand-transition>

                            <v-alert
                              v-if="selectedCategory && selectedCategory.no_show_guarantee"
                              type="warning"
                              outlined
                              dense
                              class="mt-4"
                            >
                              <small>
                                Será cobrado automaticamente o valor de uma diária para garantir a reserva em caso de não
                                comparecimento (no-show).
                              </small>
                            </v-alert>
                          </v-card-text>
                        </v-card>
                      </v-col>

                      <!-- Coluna Direita: Resumo da Reserva -->
                      <v-col
                        cols="12"
                        md="4"
                      >
                        <BookingSummaryCard
                          :selected-category="selectedCategory"
                          :search-params="searchParams"
                          :payment-method="paymentMethod"
                          :selected-installments="selectedInstallments"
                          :installment-value="installmentValue"
                          :hold-active="holdActive"
                          :hold-countdown-label="holdCountdownLabel"
                          :hold-expired="holdExpired"
                          :hold-token="holdToken"
                        >
                          <template v-slot:footer>
                            <v-btn
                              text
                              block
                              class="room-card__swap-btn"
                              color="primary"
                              @click="step = 1"
                            >
                              <v-icon
                                left
                                small
                              >
                                mdi-arrow-left
                              </v-icon>
                              Trocar quarto
                            </v-btn>
                          </template>
                        </BookingSummaryCard>
                      </v-col>
                    </v-row>

                    <v-alert
                      v-if="hasPendingReservation"
                      type="info"
                      outlined
                      dense
                      class="mt-4"
                    >
                      Já existe uma reserva aguardando pagamento. Clique em "Reabrir pagamento" para abrir novamente o link de pagamento.
                    </v-alert>

                    <div class="action-row">
                      <v-btn
                        class="back-btn"
                        color="secondary"
                        dark
                        @click="step = 2"
                      >
                        <v-icon left>
                          {{ icons.mdiArrowLeft }}
                        </v-icon>
                        Voltar
                      </v-btn>
                      <v-btn
                        color="primary"
                        :loading="loading && !hasPendingReservation"
                        :disabled="!canFinalizeReservation"
                        @click="createReservation"
                      >
                        {{ finalizeButtonLabel }}
                      </v-btn>
                    </div>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-card>
          </v-container>

          <BookingPaymentDialog
            :value="paymentDialog"
            :reservation-data="reservationData"
            :payment-method="paymentMethod"
            :payment-confirmed="paymentConfirmed"
            :hold-active="holdActive"
            :hold-countdown-label="holdCountdownLabel"
            :hold-expired="holdExpired"
            :hold-token="holdToken"
            @input="paymentDialog = $event"
            @copy-pix="copyToClipboard"
            @new-reservation="handleClosePaymentDialog"
          />

          <CategoryDetailsModal
            v-model="categoryDetailsDialog"
            :category="selectedCategoryDetails"
            @select="selectRoom"
          />
        </v-container>
      </div>

      <v-container
        v-if="hasCancellationPolicy"
        class="policy-section pb-10"
      >
        <v-card
          class="policy-card glass-card"
          elevation="0"
        >
          <v-card-title class="policy-card__title">
            <v-icon
              left
              color="primary"
            >
              mdi-shield-check
            </v-icon>
            Política de Cancelamento
          </v-card-title>
          <v-card-text>
            <p
              v-if="cancellationFreeDaysText"
              class="text--secondary mb-5"
            >
              {{ cancellationFreeDaysText }}
            </p>
            <v-row dense>
              <v-col
                v-for="(rule, index) in cancellationPolicyRules"
                :key="`policy-rule-${index}`"
                cols="12"
                md="4"
              >
                <div class="policy-rule">
                  <div class="policy-rule__title">
                    {{ rule.title }}
                  </div>
                  <div class="policy-rule__description">
                    {{ rule.description }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-container>

      <!-- Footer (oculto em modo embedded/iframe) -->
      <v-footer
        v-if="!isEmbedded"
        class="booking-footer"
        padless
      >
        <v-container class="py-8">
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <div class="footer-brand">
                {{ hotelInfo.name }}
              </div>
              <p
                v-if="hotelInfo.address"
                class="footer-text"
              >
                <v-icon
                  small
                  class="mr-2"
                >
                  mdi-map-marker
                </v-icon>
                {{ hotelInfo.address }}
              </p>
              <p class="footer-text">
                Experiência premium em hospitalidade.
              </p>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <div class="footer-title">
                Contato
              </div>
              <p
                v-if="hotelInfo.phone"
                class="footer-text"
              >
                <v-icon
                  small
                  class="mr-2"
                >
                  mdi-phone
                </v-icon>
                {{ hotelInfo.phone }}
              </p>
              <p
                v-if="hotelInfo.email"
                class="footer-text"
              >
                <v-icon
                  small
                  class="mr-2"
                >
                  mdi-email
                </v-icon>
                {{ hotelInfo.email }}
              </p>
              <p
                v-if="hotelInfo.site"
                class="footer-text"
              >
                <v-icon
                  small
                  class="mr-2"
                >
                  mdi-web
                </v-icon>
                <a
                  :href="hotelInfo.site"
                  target="_blank"
                  class="footer-link"
                >{{ hotelInfo.site }}</a>
              </p>
            </v-col>
            <v-col
              cols="12"
              md="4"
              class="footer-schedule"
            >
              <div class="footer-title">
                Horário
              </div>
              <p class="footer-text">
                Atendimento 24h / 7 dias
              </p>
            </v-col>
          </v-row>
          <v-divider
            class="my-6"
            dark
          ></v-divider>
          <div class="footer-bottom">
            <span>© {{ new Date().getFullYear() }} {{ hotelInfo.name }}</span>
            <span>Powered by Chess Hotel</span>
          </div>
        </v-container>
      </v-footer>
    </v-container>

    <div
      v-if="showConsentBanner"
      class="cookie-consent"
    >
      <div class="cookie-consent__content">
        <div class="cookie-consent__title">
          Cookies e analytics
        </div>
        <div class="cookie-consent__text">
          Usamos cookies de analytics para melhorar o motor de reservas. Você autoriza a coleta?
        </div>
      </div>
      <div class="cookie-consent__actions">
        <v-btn
          small
          color="primary"
          @click="acceptAnalyticsConsent"
        >
          Aceitar
        </v-btn>
        <v-btn
          small
          text
          @click="rejectAnalyticsConsent"
        >
          Recusar
        </v-btn>
      </div>
    </div>
  </v-app>
</template>

<script>
import * as mdiIcons from '@mdi/js'
import {
  mdiAccount,
  mdiAccountChild,
  mdiAlertCircle,
  mdiCash,
  mdiCheckCircle,
  mdiChevronRight,
  mdiArrowLeft,
  mdiArrowRight,
  mdiCreditCardOutline,
  mdiLogout,
  mdiOfficeBuilding,
  mdiRoomService,
} from '@mdi/js'
import themeConfig from '@themeConfig'
import BookingSummaryCard from './_components/BookingSummaryCard.vue'
import BookingPaymentDialog from './_components/BookingPaymentDialog.vue'
import CategoryDetailsModal from './_components/CategoryDetailsModal.vue'
import { validationRules } from '@/utils/validators'

export default {
  name: 'SearchAvailability',
  components: {
    BookingSummaryCard,
    BookingPaymentDialog,
    CategoryDetailsModal,
  },

  filters: {
    formatDate(value) {
      if (!value) return ''
      const [year, month, day] = value.split('-')

      return `${day}/${month}/${year}`
    },
  },

  data() {
    return {
      summaryIcon: mdiRoomService,
      icons: {
        mdiAccount,
        mdiAccountChild,
        mdiAlertCircle,
        mdiCash,
        mdiCheckCircle,
        mdiChevronRight,
        mdiArrowLeft,
        mdiArrowRight,
        mdiCreditCardOutline,
        mdiLogout,
        mdiOfficeBuilding,
      },
      step: 1,
      loading: false,
      searched: false,
      bookingEnabled: true,
      checkInMenu: false,
      checkOutMenu: false,
      paymentDialog: false,
      isEmbedded: false, // Modo iframe (sem header/footer)
      corporateLoading: false,
      corporateCredentials: {
        email: '',
        password: '',
      },
      corporateToken: null,
      corporateCompany: null,
      corporateBilling: null,
      billingCheckoutScope: 'accommodation',

      hotelInfo: {
        name: 'Chess Hotel',
        logo: null,
        phone: null,
        email: null,
        site: null,
        address: null,
      },
      highlights: [
        { icon: 'mdi-shield-check', text: 'Cancelamento flexível' },
        { icon: 'mdi-lightning-bolt', text: 'Confirmação imediata' },
        { icon: 'mdi-star-outline', text: 'Benefícios exclusivos' },
      ],

      searchParams: {
        check_in: null,
        check_out: null,
        adults: 1,
        children: 0,
      },

      guestData: {
        name: '',
        cpf: '',
        email: '',
        phone: '',
      },
      childAges: [],
      specialRequests: '',

      cardData: {
        number: '',
        holder_name: '',
        holder_cpf: '',
        expiry_month: '',
        expiry_year: '',
        ccv: '',
      },

      paymentMethod: 'pix',
      paymentMethods: [], // Métodos configurados no motor
      availableRooms: [],
      selectedCategory: null,
      reservationData: null,

      // Configurações de parcelamento
      maxInstallments: 12,
      minInstallmentValue: 0,
      selectedInstallments: 1,

      // Configuração de hold (tempo de pagamento)
      paymentHoldMinutes: 20,

      // Regras de validação importadas de utils/validators.js
      rules: validationRules,
      holdToken: null,
      holdExpiresAt: null,
      holdCountdown: 0,
      holdInterval: null,
      holdExpired: false,
      paymentConfirmed: false,
      socketHandlers: {},

      // Modal de detalhes da categoria
      categoryDetailsDialog: false,
      selectedCategoryDetails: null,
      analyticsEnabled: false,
      gtmContainerId: null,
      analyticsConsent: null,
      gtmLoaded: false,
      lastTrackedPurchaseId: null,
    }
  },

  computed: {
    isCorporateFlow() {
      return this.$route.path === '/reservas-empresa'
    },
    isCorporateAuthenticated() {
      return !this.isCorporateFlow || !!this.corporateToken
    },
    corporateCompanyDisplayName() {
      const company = this.corporateCompany || {}

      return company.fantasy_name || company.nome_fantasia || company.razao_social || 'Empresa corporativa'
    },
    corporateShowBillingBalance() {
      return Boolean(this.corporateCompany?.booking_engine_show_billing_balance ?? true)
    },
    minCheckIn() {
      return new Date().toISOString().substr(0, 10)
    },
    minCheckOut() {
      const baseline = this.searchParams.check_in
        ? this.getNextDay(this.searchParams.check_in)
        : this.getNextDay(this.minCheckIn)

      return baseline || this.minCheckIn
    },
    isGuestDataValid() {
      // Valida CPF
      const cpfValid = this.rules.cpf(this.guestData.cpf) === true

      // Valida email
      const emailValid = this.rules.email(this.guestData.email) === true

      const childAgesValid = this.childAges.every(age => this.rules.childAge(age) === true)

      return (
        this.guestData.name
        && cpfValid
        && emailValid
        && this.guestData.phone
        && childAgesValid
      )
    },
    availablePaymentMethods() {
      const methodLabels = {
        pix: 'PIX',
        boleto: 'Boleto Bancário',
        cartao: 'Cartão de Crédito',
        debito: 'Cartão de Débito',
        faturamento: 'Faturamento Corporativo',
      }

      return this.paymentMethods.map(method => ({
        value: method,
        label: methodLabels[method] || method,
      }))
    },
    hasPendingReservation() {
      return !!this.reservationData && !this.paymentConfirmed && !this.holdExpired
    },
    corporateBillingAvailableLabel() {
      const available = this.corporateBilling?.credit_available
      if (available === null || available === undefined || Number.isNaN(Number(available))) {
        return 'não disponível no momento'
      }

      return this.formatCurrency(Number(available))
    },
    corporateBillingConsumedLabel() {
      const consumed = Number(this.corporateBilling?.credit_consumed || 0)

      return this.formatCurrency(consumed)
    },
    corporateBillingLimitLabel() {
      const limit = Number(this.corporateBilling?.billing_limit || 0)
      if (limit <= 0) {
        return 'Sem limite definido'
      }

      return this.formatCurrency(limit)
    },
    corporateCreditUsagePercent() {
      const consumed = Number(this.corporateBilling?.credit_consumed || 0)
      const limit = Number(this.corporateBilling?.billing_limit || 0)
      if (limit <= 0) {
        return 0
      }

      return Math.max(0, Math.min(100, Math.round((consumed / limit) * 100)))
    },
    corporateCreditBarColor() {
      if (this.corporateBilling?.credit_blocked) return 'error'
      if (this.corporateCreditUsagePercent >= 90) return 'error'
      if (this.corporateCreditUsagePercent >= 75) return 'warning'

      return 'success'
    },
    finalizeButtonLabel() {
      return this.hasPendingReservation ? 'Reabrir pagamento' : 'Finalizar Reserva'
    },
    canFinalizeReservation() {
      if (this.hasPendingReservation) {
        return true
      }

      if (this.holdExpired && this.holdToken) {
        return false
      }

      // Todos os métodos podem finalizar
      // Para cartão, cliente pagará na página segura do Asaas
      // Para PIX/Boleto, continua o fluxo atual
      return true
    },
    paymentAmount() {
      // Valor a ser pago (antecipado ou total)
      if (!this.selectedCategory) return 0

      return this.selectedCategory.require_payment
        ? this.selectedCategory.advance_payment
        : this.selectedCategory.total_price
    },
    availableInstallments() {
      // Retorna array de opções de parcelas baseado nas configurações
      const amount = this.paymentAmount
      if (amount <= 0) return []

      const maxConfigured = this.maxInstallments
      const minValue = this.minInstallmentValue || 0

      // Se valor mínimo = 0, usa o máximo configurado
      let maxAllowed = maxConfigured

      // Se tem valor mínimo, calcula quantas parcelas cabem
      if (minValue > 0) {
        const maxByValue = Math.floor(amount / minValue)
        maxAllowed = Math.min(maxConfigured, maxByValue)
      }

      // Se não couber nem 1 parcela, retorna vazio
      if (maxAllowed < 1) return []

      // Gera array de opções
      return Array.from({ length: maxAllowed }, (_, index) => {
        const installmentNumber = index + 1
        const installmentValue = amount / installmentNumber
        const text = installmentNumber === 1
          ? `1x de ${this.formatCurrency(amount)} sem juros`
          : `${installmentNumber}x de ${this.formatCurrency(installmentValue)} sem juros`

        return {
          value: installmentNumber,
          text,
        }
      })
    },
    holdActive() {
      return !!this.holdToken && !this.holdExpired && this.holdCountdown > 0
    },
    holdCountdownLabel() {
      if (!this.holdActive) {
        return '--:--'
      }
      const minutes = Math.floor(this.holdCountdown / 60)
      const seconds = this.holdCountdown % 60

      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    installmentValue() {
      // Calcula o valor de cada parcela
      const amount = this.paymentAmount
      if (!amount || this.selectedInstallments <= 0) return 0

      return amount / this.selectedInstallments
    },
    installmentHint() {
      // Hint explicativo sobre as parcelas
      if (this.minInstallmentValue > 0) {
        return `Parcela mínima: ${this.formatCurrency(this.minInstallmentValue)}`
      }

      return 'Parcelamento sem juros'
    },
    totalGuests() {
      const adults = Number(this.searchParams.adults || 0)
      const children = Number(this.searchParams.children || 0)

      return adults + children
    },
    totalGuestsLabel() {
      const total = this.totalGuests

      return total === 1 ? '1 pessoa' : `${total} pessoas`
    },
    nightsCount() {
      if (this.selectedCategory && Number(this.selectedCategory.days)) {
        return Number(this.selectedCategory.days)
      }
      const { check_in: checkIn, check_out: checkOut } = this.searchParams || {}
      if (!checkIn || !checkOut) {
        return 0
      }
      const start = new Date(checkIn)
      const end = new Date(checkOut)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return 0
      }
      const diff = Math.round((end.getTime() - start.getTime()) / 86400000)

      return diff > 0 ? diff : 0
    },
    nightsLabel() {
      const nights = this.nightsCount
      if (!nights) {
        return 'Selecione datas'
      }

      return nights === 1 ? '1 noite' : `${nights} noites`
    },
    hasCancellationPolicy() {
      const policy = this.hotelInfo && this.hotelInfo.cancellation_policy

      return policy && Array.isArray(policy.rules) && policy.rules.length > 0
    },
    cancellationPolicyRules() {
      if (!this.hasCancellationPolicy) {
        return []
      }

      return this.hotelInfo.cancellation_policy.rules
    },
    cancellationFreeDaysText() {
      if (!this.hasCancellationPolicy) {
        return ''
      }
      const policy = this.hotelInfo.cancellation_policy || {}
      const freeDays = typeof policy.free_days === 'number' ? policy.free_days : 0
      if (freeDays <= 0) {
        return ''
      }
      const label = freeDays === 1 ? 'dia' : 'dias'

      return `Cancelamento gratuito até ${freeDays} ${label} antes do check-in.`
    },
    bookingColors() {
      const defaults = {
        heroStart: '#0d47a1',
        heroEnd: '#1e88e5',
        backgroundTop: '#e3f2fd',
        backgroundMid: '#f7f9fc',
        backgroundBottom: '#ffffff',
        cardStart: '#ffffff',
        cardEnd: '#e8f1ff',
        textPrimary: '#1f2a37',
        textSecondary: '#5d6473',
        outline: '#dce7f6',
        accent: '#ffb703',
        success: '#2E7D32',
        roomCardStart: '#ffffff',
        roomCardEnd: '#f9fbff',
      }

      return {
        ...defaults,
        ...(themeConfig.bookingEngine || {}),
      }
    },
    paletteStyle() {
      const c = this.bookingColors

      return {
        '--hero-start': c.heroStart,
        '--hero-end': c.heroEnd,
        '--bg-top': c.backgroundTop,
        '--bg-mid': c.backgroundMid,
        '--bg-bottom': c.backgroundBottom,
        '--card-start': c.cardStart,
        '--card-end': c.cardEnd,
        '--text-primary': c.textPrimary,
        '--text-muted': c.textSecondary,
        '--outline-color': c.outline,
        '--accent-color': c.accent,
        '--success-color': c.success,
        '--room-card-start': c.roomCardStart,
        '--room-card-end': c.roomCardEnd,
      }
    },
    apiBasePath() {
      return this.isCorporateFlow ? 'public/company-booking-engine' : 'public/booking-engine'
    },
    canTrackAnalytics() {
      return this.analyticsEnabled && this.analyticsConsent === 'accepted'
    },
    showConsentBanner() {
      return this.analyticsEnabled && this.analyticsConsent === null
    },
  },

  watch: {
    paymentMethod(newMethod) {
      // Resetar parcelas quando mudar o método de pagamento
      if (newMethod !== 'cartao') {
        this.selectedInstallments = 1
      }
      if (newMethod !== 'faturamento') {
        this.billingCheckoutScope = 'accommodation'
      }
      if (this.step >= 3 && this.selectedCategory) {
        this.trackAddPaymentInfo()
      }
    },
    selectedCategory() {
      // Resetar parcelas quando mudar a categoria
      this.selectedInstallments = 1
    },
    'searchParams.check_in': function handleSearchCheckInChange(newDate) {
      if (!newDate) {
        this.searchParams.check_out = null

        return
      }
      const nextDay = this.getNextDay(newDate)
      if (!nextDay) {
        return
      }
      if (!this.searchParams.check_out) {
        this.searchParams.check_out = nextDay

        return
      }
      const currentCheckOut = new Date(this.searchParams.check_out)
      const currentCheckIn = new Date(newDate)
      if (currentCheckOut <= currentCheckIn) {
        this.searchParams.check_out = nextDay
      }
    },
    'searchParams.children': {
      immediate: true,
      handler(newValue) {
        this.syncChildAges(newValue)
      },
    },
  },
  created() {
    this.setupSocketListeners()
    if (this.$route.path === '/reservas-empresa') {
      window.addEventListener('beforeunload', this.handleCorporateBeforeUnload)
    }
  },

  beforeDestroy() {
    if (this.$route.path === '/reservas-empresa') {
      window.removeEventListener('beforeunload', this.handleCorporateBeforeUnload)
    }
    this.teardownSocketListeners()
    this.clearHoldTimer()
    this.releaseActiveHold()
  },

  async mounted() {
    this.analyticsConsent = window.localStorage.getItem('bookingEngineAnalyticsConsent')

    // Detectar modo embedded (iframe)
    const urlParams = new URLSearchParams(window.location.search)
    this.isEmbedded = urlParams.get('embedded') === 'true' || urlParams.get('iframe') === 'true'

    const initialSearch = this.parseSearchParamsFromUrl(urlParams)
    if (initialSearch.params) {
      this.searchParams = {
        ...this.searchParams,
        ...initialSearch.params,
      }
    }

    if (this.isCorporateFlow) {
      this.corporateToken = window.sessionStorage.getItem('companyBookingEngineToken')
      window.localStorage.removeItem('companyBookingEngineToken')
      if (this.corporateToken) {
        await this.fetchCorporateProfile()
      }
    }

    if (this.isCorporateAuthenticated) {
      await this.loadHotelInfo()
      await this.checkBookingStatus()
      await this.loadPaymentMethods()
    }

    if (initialSearch.shouldSearch) {
      await this.searchAvailability()
    }

    // Comunicar altura ao parent (se em iframe)
    if (this.isEmbedded && window.parent !== window) {
      this.$nextTick(() => {
        this.sendHeightToParent()
      })
    }
  },

  methods: {
    ensureDataLayer() {
      window.dataLayer = window.dataLayer || []

      return window.dataLayer
    },
    loadGtm() {
      if (!this.analyticsEnabled || !this.gtmContainerId || this.gtmLoaded) {
        return
      }
      const existing = document.getElementById('booking-engine-gtm-script')
      if (existing) {
        this.gtmLoaded = true

        return
      }
      this.ensureDataLayer()
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
      const script = document.createElement('script')
      script.id = 'booking-engine-gtm-script'
      script.async = true
      script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmContainerId}`
      document.head.appendChild(script)
      this.gtmLoaded = true
    },
    pushEvent(event, payload = {}) {
      if (!this.canTrackAnalytics) {
        return
      }
      this.ensureDataLayer().push({
        event,
        ...payload,
      })
    },
    getEcommerceItem(category = this.selectedCategory) {
      if (!category) {
        return null
      }

      return {
        item_id: String(category.id),
        item_name: category.name,
        item_category: 'Hospedagem',
        quantity: 1,
        price: Number(category.total_price || 0),
      }
    },
    getBookingContextPayload() {
      const item = this.getEcommerceItem()
      const totalValue = Number(this.selectedCategory?.total_price || 0)

      return {
        currency: 'BRL',
        value: totalValue,
        check_in: this.searchParams.check_in,
        check_out: this.searchParams.check_out,
        nights: this.nightsCount,
        adults: Number(this.searchParams.adults || 0),
        children: Number(this.searchParams.children || 0),
        payment_method: this.paymentMethod,
        items: item ? [item] : [],
      }
    },
    acceptAnalyticsConsent() {
      this.analyticsConsent = 'accepted'
      window.localStorage.setItem('bookingEngineAnalyticsConsent', 'accepted')
      this.loadGtm()
    },
    rejectAnalyticsConsent() {
      this.analyticsConsent = 'rejected'
      window.localStorage.setItem('bookingEngineAnalyticsConsent', 'rejected')
    },
    goToCheckoutStep() {
      this.step = 3
      this.pushEvent('begin_checkout', {
        ecommerce: this.getBookingContextPayload(),
      })
      this.trackAddPaymentInfo()
    },
    syncChildAges(childrenCount) {
      const totalChildren = Math.max(0, Number(childrenCount || 0))
      const nextAges = Array.from({ length: totalChildren }, (_, index) => {
        const currentValue = this.childAges[index]

        return currentValue === undefined || currentValue === null || currentValue === ''
          ? null
          : currentValue
      })

      this.childAges = nextAges
    },
    trackAddPaymentInfo() {
      this.pushEvent('add_payment_info', {
        ecommerce: this.getBookingContextPayload(),
      })
    },
    trackPurchase() {
      const reserveId = this.reservationData?.reserve_id
      if (!reserveId || this.lastTrackedPurchaseId === reserveId) {
        return
      }
      this.lastTrackedPurchaseId = reserveId
      const ecommerce = this.getBookingContextPayload()
      ecommerce.transaction_id = String(reserveId)
      ecommerce.paid_amount = Number(this.reservationData?.amount || 0)
      this.pushEvent('purchase', { ecommerce })
    },
    apiUrl(path) {
      return `${this.apiBasePath}/${path}`
    },
    authHeaders() {
      if (!this.isCorporateFlow || !this.corporateToken) {
        return {}
      }

      return {
        Authorization: `Bearer ${this.corporateToken}`,
      }
    },
    async loginCorporate() {
      if (!this.corporateCredentials.email || !this.corporateCredentials.password) {
        this.$swal.fire('Atenção', 'Informe e-mail e senha para continuar.', 'warning')

        return
      }

      this.corporateLoading = true
      try {
        const response = await this.$http.post(this.apiUrl('login'), this.corporateCredentials)
        const token = response?.data?.data?.authorization?.token
        if (!token) {
          throw new Error('Token nao retornado')
        }

        this.corporateToken = token
        window.sessionStorage.setItem('companyBookingEngineToken', token)
        await this.fetchCorporateProfile()
        await this.loadHotelInfo()
        await this.checkBookingStatus()
        await this.loadPaymentMethods()
      } catch (error) {
        const message = error?.response?.data?.message || 'Não foi possível autenticar o hóspede corporativo.'
        this.$swal.fire('Erro', message, 'error')
      } finally {
        this.corporateLoading = false
      }
    },
    async fetchCorporateProfile() {
      if (!this.corporateToken) {
        return
      }

      try {
        const response = await this.$http.get(this.apiUrl('me'), { headers: this.authHeaders() })
        this.corporateCompany = response?.data?.data?.company || null
      } catch (error) {
        this.corporateToken = null
        this.corporateCompany = null
        window.sessionStorage.removeItem('companyBookingEngineToken')
        window.localStorage.removeItem('companyBookingEngineToken')
      }
    },
    async forgotCorporatePassword() {
      if (!this.corporateCredentials.email) {
        this.$swal.fire('Atenção', 'Informe o e-mail corporativo para recuperar a senha.', 'warning')

        return
      }

      this.corporateLoading = true
      try {
        const response = await this.$http.post(this.apiUrl('forgot-password'), {
          email: this.corporateCredentials.email,
        })
        this.$swal.fire('Sucesso', response?.data?.message || 'Se o e-mail estiver cadastrado, enviaremos uma nova senha.', 'success')
      } catch (error) {
        this.$swal.fire('Erro', 'Não foi possível solicitar nova senha agora.', 'error')
      } finally {
        this.corporateLoading = false
      }
    },
    async logoutCorporate() {
      if (!this.corporateToken) {
        this.clearCorporateSession()

        return
      }

      this.corporateLoading = true
      try {
        await this.$http.post(this.apiUrl('logout'), {}, { headers: this.authHeaders() })
      } catch (error) {
        // segue o fluxo de logout local mesmo se a API falhar
      } finally {
        this.clearCorporateSession()
        this.corporateLoading = false
      }
    },
    clearCorporateSession() {
      this.corporateToken = null
      this.corporateCompany = null
      this.corporateCredentials.password = ''
      window.sessionStorage.removeItem('companyBookingEngineToken')
      window.localStorage.removeItem('companyBookingEngineToken')
    },

    // Comunicar altura ao iframe pai (modo embedded)
    sendHeightToParent() {
      if (window.parent !== window) {
        const height = document.documentElement.scrollHeight
        window.parent.postMessage({
          type: 'resize',
          height,
        }, '*')
      }
    },

    parseSearchParamsFromUrl(urlParams) {
      const checkIn = this.normalizeDateParam(urlParams.get('check_in'))
      const rawCheckOut = this.normalizeDateParam(urlParams.get('check_out'))
      const adults = this.normalizeNumberParam(urlParams.get('adults'), { min: 1 })
      const children = this.normalizeNumberParam(urlParams.get('children'), { min: 0 })

      const params = {}
      if (checkIn) {
        params.check_in = checkIn
      }

      if (checkIn && rawCheckOut && this.areSearchDatesValid(checkIn, rawCheckOut)) {
        params.check_out = rawCheckOut
      }

      if (adults !== null) {
        params.adults = adults
      }

      if (children !== null) {
        params.children = children
      }

      return {
        params: Object.keys(params).length ? params : null,
        shouldSearch: Boolean(params.check_in && params.check_out),
      }
    },

    normalizeDateParam(value) {
      if (!value) {
        return null
      }
      const trimmed = String(value).trim()
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return null
      }
      const date = new Date(trimmed)
      if (Number.isNaN(date.getTime())) {
        return null
      }
      const normalized = date.toISOString().substr(0, 10)

      return normalized === trimmed ? trimmed : null
    },

    normalizeNumberParam(value, { min = null, max = null } = {}) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const parsed = Number.parseInt(value, 10)
      if (Number.isNaN(parsed)) {
        return null
      }
      let result = parsed
      if (min !== null) {
        result = Math.max(min, result)
      }
      if (max !== null) {
        result = Math.min(max, result)
      }

      return result
    },

    areSearchDatesValid(checkIn, checkOut) {
      if (!checkIn || !checkOut) {
        return false
      }
      const checkInDate = new Date(checkIn)
      const checkOutDate = new Date(checkOut)
      if (Number.isNaN(checkInDate.getTime()) || Number.isNaN(checkOutDate.getTime())) {
        return false
      }

      return checkOutDate > checkInDate
    },

    setupSocketListeners() {
      if (!this.$socket) {
        return
      }

      this.socketHandlers.reservations = payload => this.handleReservationSocket(payload)
      this.$socket.on('reservations', this.socketHandlers.reservations)
    },
    teardownSocketListeners() {
      if (!this.$socket || !this.socketHandlers.reservations) {
        return
      }

      this.$socket.off('reservations', this.socketHandlers.reservations)
      this.socketHandlers.reservations = null
    },
    handleReservationSocket(payload) {
      if (!payload || !payload.data || !this.reservationData) {
        return
      }

      const { event, data } = payload
      if (!data || data.reserve_id !== this.reservationData.reserve_id) {
        return
      }

      if (event === 'payment-received') {
        if (!this.paymentConfirmed) {
          this.paymentConfirmed = true
          this.clearHoldTimer()
          this.holdToken = null
          this.$swal.fire('Pagamento identificado', 'Recebemos a confirmação do pagamento.', 'success')
          this.trackPurchase()
        }

        return
      }

      if (event === 'reserve-updated') {
        if (data.status === 'confirmed') {
          this.paymentConfirmed = true
          this.clearHoldTimer()
          this.holdToken = null
          this.trackPurchase()
        }

        if (data.status === 'canceled' && data.source === 'hold-expired') {
          this.handleHoldExpired(false, true)
        }
      }
    },
    startHoldTimer(expiresAt) {
      this.clearHoldTimer()

      if (!expiresAt) {
        return
      }

      const target = new Date(expiresAt).getTime()
      if (Number.isNaN(target)) {
        return
      }

      const diff = Math.floor((target - Date.now()) / 1000)
      if (diff <= 0) {
        this.handleHoldExpired(true, true)

        return
      }

      this.holdCountdown = diff
      this.holdInterval = setInterval(() => {
        this.holdCountdown -= 1
        if (this.holdCountdown <= 0) {
          this.handleHoldExpired(true, true)
        }
      }, 1000)
    },
    clearHoldTimer() {
      if (this.holdInterval) {
        clearInterval(this.holdInterval)
        this.holdInterval = null
      }
      this.holdCountdown = 0
    },
    async expireHoldOnServer() {
      if (!this.holdToken) {
        return null
      }
      try {
        const response = await this.$http.post(this.apiUrl(`hold/${this.holdToken}/expire`), {}, { headers: this.authHeaders() })

        return response.data
      } catch (error) {
        console.error('Erro ao expirar hold:', error)

        return null
      } finally {
        this.holdToken = null
        this.holdExpiresAt = null
      }
    },
    async handleHoldExpired(triggerApi = true, notifyUser = true) {
      if (this.holdExpired) {
        return
      }

      this.holdExpired = true
      this.clearHoldTimer()

      let apiResult = null
      if (triggerApi) {
        apiResult = await this.expireHoldOnServer()
      } else {
        this.holdToken = null
        this.holdExpiresAt = null
      }

      if (apiResult?.data?.payment_confirmed) {
        this.holdExpired = false
        this.paymentConfirmed = true
        this.$swal.fire(
          'Pagamento confirmado',
          'Identificamos o pagamento antes de cancelar a reserva.',
          'success',
        )
        await this.checkBookingStatus()
        this.trackPurchase()

        return
      }

      if (notifyUser) {
        this.pushEvent('hold_expired', {
          reserve_id: this.reservationData?.reserve_id ? String(this.reservationData.reserve_id) : null,
        })
        this.$swal.fire(
          'Tempo expirado',
          'O tempo para concluir o pagamento terminou. Refaça a busca para tentar novamente.',
          'warning',
        )
        await this.resetForm()
        this.step = 1
      }
    },
    async releaseActiveHold() {
      if (!this.holdToken) {
        return
      }

      await this.expireHoldOnServer()
      this.clearHoldTimer()
      this.holdExpired = false
    },
    setHoldMetadata(reservationPayload) {
      const holdInfo = reservationPayload?.hold || {}
      const token = reservationPayload?.hold_token || holdInfo.hold_token
      const expires = reservationPayload?.hold_expires_at || holdInfo.hold_expires_at

      this.holdToken = token || null
      this.holdExpiresAt = expires || null
      this.holdExpired = false

      if (this.holdToken && this.holdExpiresAt && !this.paymentConfirmed) {
        this.startHoldTimer(this.holdExpiresAt)
      } else {
        this.clearHoldTimer()
      }
    },
    handleAvailabilityLost() {
      this.$swal.fire(
        'Disponibilidade atualizada',
        'O quarto selecionado não está mais disponível. Refaça a busca para escolher outra opção.',
        'warning',
      )
      this.step = 1
      this.paymentDialog = false
      this.reservationData = null
      this.clearHoldTimer()
      this.holdToken = null
      this.holdExpiresAt = null
      this.holdExpired = false
    },
    async loadHotelInfo() {
      try {
        const response = await this.$http.get(this.apiUrl('hotel-info'), { headers: this.authHeaders() })
        if (response.data.success) {
          this.hotelInfo = response.data.data
        }
      } catch (error) {
        console.error('Erro ao carregar informações do hotel:', error)
      }
    },

    async checkBookingStatus() {
      try {
        const response = await this.$http.get(this.apiUrl('status'), { headers: this.authHeaders() })
        this.bookingEnabled = response.data.enabled
      } catch (error) {
        console.error('Erro ao verificar status:', error)
        this.$swal.fire('Erro', 'Erro ao verificar status do motor de reservas', 'error')
      }
    },

    async loadPaymentMethods() {
      try {
        const response = await this.$http.get(this.apiUrl('status'), { headers: this.authHeaders() })
        this.analyticsEnabled = Boolean(response.data.analytics_enabled)
        this.gtmContainerId = response.data.gtm_container_id || null
        if (this.analyticsEnabled && this.analyticsConsent === 'accepted') {
          this.loadGtm()
        }
        if (response.data.payment_methods && response.data.payment_methods.length > 0) {
          this.paymentMethods = response.data.payment_methods

          // Define o primeiro método como padrão
          const [firstPaymentMethod] = this.paymentMethods
          this.paymentMethod = firstPaymentMethod
        }

        this.corporateCompany = response.data.company || this.corporateCompany
        this.corporateBilling = response.data?.company?.billing || null

        // Carregar configurações de parcelamento
        if (response.data.max_installments) {
          this.maxInstallments = response.data.max_installments
        }
        if (response.data.min_installment_value !== undefined) {
          this.minInstallmentValue = response.data.min_installment_value
        }

        // Carregar configuração de tempo de hold (tempo para pagamento)
        if (response.data.payment_hold_minutes) {
          this.paymentHoldMinutes = response.data.payment_hold_minutes
        }
      } catch (error) {
        console.error('Erro ao carregar métodos de pagamento:', error)
      }
    },

    async searchAvailability() {
      if (!this.searchParams.check_in || !this.searchParams.check_out) {
        this.$swal.fire('Atenção', 'Selecione as datas de check-in e check-out', 'warning')

        return
      }

      this.loading = true
      this.step = 1
      this.searched = false
      this.availableRooms = []
      this.selectedCategory = null
      this.selectedCategoryDetails = null
      this.categoryDetailsDialog = false

      try {
        const response = await this.$http.post(this.apiUrl('availability'), this.searchParams, { headers: this.authHeaders() })

        if (response.data.success) {
          this.availableRooms = response.data.data
          this.searched = true
          this.pushEvent('view_item_list', {
            ecommerce: {
              item_list_name: 'Resultados de disponibilidade',
              items: this.availableRooms.map(room => ({
                item_id: String(room.id),
                item_name: room.name,
                item_category: 'Hospedagem',
                price: Number(room.total_price || 0),
                quantity: 1,
              })),
            },
          })
        }
      } catch (error) {
        console.error('Erro ao buscar disponibilidade:', error)
        const message = (error.response && error.response.data && error.response.data.message) || 'Erro ao buscar disponibilidade'
        this.pushEvent('search_error', { message })
        this.$swal.fire('Erro', message, 'error')
      } finally {
        this.loading = false
      }
    },

    selectRoom(category) {
      this.selectedCategory = category
      this.categoryDetailsDialog = false
      this.step = 2
      this.pushEvent('add_to_cart', {
        ecommerce: this.getBookingContextPayload(),
      })
    },

    openCategoryDetails(category) {
      this.selectedCategoryDetails = category
      this.categoryDetailsDialog = true
    },

    async createReservation() {
      if (this.hasPendingReservation) {
        this.paymentDialog = true

        return
      }

      const stillAvailable = await this.confirmAvailabilityBeforeReservation()
      if (!stillAvailable) {
        return
      }

      this.loading = true

      try {
        const payload = {
          room_category_id: this.selectedCategory.id,
          check_in: this.searchParams.check_in,
          check_out: this.searchParams.check_out,
          adults: this.searchParams.adults,
          children: this.searchParams.children,
          guest: this.guestData,
          payment_method: this.paymentMethod,
          total_amount: parseFloat(this.selectedCategory.total_price),
          child_ages: this.childAges.filter(age => age !== null && age !== ''),
          special_requests: this.specialRequests,
        }

        if (this.paymentMethod === 'faturamento') {
          payload.billing_checkout_scope = this.billingCheckoutScope || 'accommodation'
        }

        // Adicionar número de parcelas para cartão de crédito
        // (Cliente digitará dados do cartão na página segura do Asaas)
        if (this.paymentMethod === 'cartao') {
          payload.installments = this.selectedInstallments || 1
        }

        const response = await this.$http.post(this.apiUrl('create'), payload, { headers: this.authHeaders() })

        if (response.data.success) {
          this.reservationData = response.data.data
          this.paymentDialog = this.paymentMethod !== 'faturamento'
          this.paymentConfirmed = this.paymentMethod === 'faturamento'
          this.holdExpired = false
          if (this.paymentMethod !== 'faturamento') {
            this.setHoldMetadata(this.reservationData)
          }

          // Para cartão/débito, cliente será redirecionado para invoiceUrl
          // Para PIX/Boleto, mostra QR Code/Código de barras no dialog
          if (this.paymentMethod === 'faturamento') {
            this.$swal.fire('Sucesso', 'Reserva corporativa criada com faturamento.', 'success')
            this.trackPurchase()
            await this.resetForm()
          }
        }
      } catch (error) {
        console.error('Erro ao criar reserva:', error)
        const message = (error.response && error.response.data && error.response.data.message) || 'Erro ao criar reserva'
        this.pushEvent('payment_failed', { message, payment_method: this.paymentMethod })
        this.$swal.fire('Erro', message, 'error')
      } finally {
        this.loading = false
      }
    },
    async confirmAvailabilityBeforeReservation() {
      try {
        const response = await this.$http.post(this.apiUrl('availability'), this.searchParams, { headers: this.authHeaders() })
        if (response.data.success) {
          const categories = response.data.data || []
          this.availableRooms = categories
          const updatedCategory = categories.find(item => item.id === this.selectedCategory?.id)
          if (!updatedCategory || (updatedCategory.available_rooms ?? 0) < 1) {
            this.handleAvailabilityLost()

            return false
          }
          this.selectedCategory = updatedCategory

          return true
        }
      } catch (error) {
        console.error('Erro ao validar disponibilidade:', error)
      }

      this.$swal.fire('Indisponível', 'Não foi possível confirmar a disponibilidade. Tente novamente.', 'warning')

      return false
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$swal.fire({
          icon: 'success',
          title: 'Copiado!',
          text: 'Código PIX copiado para a área de transferência',
          timer: 2000,
          showConfirmButton: false,
        })
      })
    },

    async resetForm() {
      await this.releaseActiveHold()
      this.clearHoldTimer()
      this.step = 1
      this.paymentDialog = false
      this.availableRooms = []
      this.selectedCategory = null
      this.reservationData = null
      this.searched = false
      this.searchParams = {
        check_in: null,
        check_out: null,
        adults: 1,
        children: 0,
      }
      this.guestData = {
        name: '',
        cpf: '',
        email: '',
        phone: '',
      }
      this.childAges = []
      this.specialRequests = ''
      this.cardData = {
        number: '',
        holder_name: '',
        holder_cpf: '',
        expiry_month: '',
        expiry_year: '',
        ccv: '',
      }
      this.selectedInstallments = 1
      this.holdToken = null
      this.holdExpiresAt = null
      this.holdCountdown = 0
      this.holdExpired = false
      this.paymentConfirmed = false

      // Resetar para o primeiro método de pagamento disponível
      if (this.paymentMethods.length > 0) {
        const [firstPaymentMethod] = this.paymentMethods
        this.paymentMethod = firstPaymentMethod
      }
    },
    async handleClosePaymentDialog() {
      this.paymentDialog = false
      if (this.paymentConfirmed) {
        await this.resetForm()
      }
    },
    getNextDay(dateString) {
      if (!dateString) {
        return null
      }
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) {
        return null
      }
      date.setDate(date.getDate() + 1)

      return date.toISOString().substr(0, 10)
    },
    formatOccupancy(category) {
      if (!category) return ''
      const adults = Number(category.max_adults || 0)
      const children = Number(category.max_children || 0)
      if (adults || children) {
        const parts = []
        if (adults) {
          parts.push(`${adults} ${adults === 1 ? 'adulto' : 'adultos'}`)
        }
        if (children) {
          parts.push(`${children} ${children === 1 ? 'criança' : 'crianças'}`)
        }

        return parts.join(' e ')
      }
      const maxGuests = Number(category.max_guests || 0)
      if (maxGuests) {
        return `${maxGuests} ${maxGuests === 1 ? 'pessoa' : 'pessoas'}`
      }

      return ''
    },
    cancellationPolicyLabel() {
      if (!this.hasCancellationPolicy) {
        return 'Cancelamento sob consulta'
      }

      return 'Permite cancelamento'
    },
    cancellationPolicyIcon() {
      return this.hasCancellationPolicy ? this.icons.mdiCheckCircle : this.icons.mdiAlertCircle
    },
    cancellationPolicyColor() {
      return this.hasCancellationPolicy ? 'success' : 'warning'
    },
    adultIconCount(category) {
      const adults = Math.floor(Number(category?.max_adults || 0))

      return adults > 0 ? adults : 0
    },
    childIconCount(category) {
      const children = Math.floor(Number(category?.max_children || 0))

      return children > 0 ? children : 0
    },
    resolveMdiIcon(name) {
      if (!name) return null
      if (name.startsWith('mdi')) {
        if (!name.includes('-')) return mdiIcons[name] || null
        const key = `mdi${name
          .replace(/^mdi-/, '')
          .split('-')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join('')}`

        return mdiIcons[key] || null
      }

      return null
    },
    categoryPolicyAmenities(category) {
      const amenities = Array.isArray(category?.main_amenities) ? category.main_amenities : []

      return amenities.filter(amenity => {
        const group = String(amenity?.group || '').toLowerCase()

        return group === 'general' || group === 'policy'
      })
    },
    amenityLines(category) {
      const grouped = Array.isArray(category?.grouped_amenities) ? category.grouped_amenities : []
      const byKey = grouped.reduce((acc, group) => {
        const key = String(group?.group || '').toLowerCase()
        acc[key] = Array.isArray(group?.items) ? group.items : []

        return acc
      }, {})

      const generalItems = byKey.general || []
      const roomItems = byKey.room || []
      const otherItems = grouped
        .filter(group => !['general', 'room'].includes(String(group?.group || '').toLowerCase()))
        .flatMap(group => group.items || [])

      const buildLine = (key, label, items) => {
        const limited = items.slice(0, 4)

        return {
          key,
          label,
          items: limited,
          moreCount: Math.max(items.length - limited.length, 0),
        }
      }

      const lines = []
      if (generalItems.length) lines.push(buildLine('general', 'Gerais', generalItems))
      if (roomItems.length) lines.push(buildLine('room', 'Quarto', roomItems))
      if (otherItems.length) lines.push(buildLine('other', 'Outras', otherItems))

      return lines
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return 'R$ 0,00'
      }
      const number = Number(value)

      return number.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    formatDisplayDate(value) {
      if (!value) return '--/--/----'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return '--/--/----'
      }
      const formatter = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      const formatted = formatter.format(date)

      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    },
    handleCorporateBeforeUnload() {
      if (!this.isCorporateFlow || !this.corporateToken) {
        return
      }

      try {
        fetch('/api/public/company-booking-engine/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.corporateToken}`,
          },
          keepalive: true,
        })
      } catch (error) {
        // ignora erros no fechamento da pagina
      }

      this.clearCorporateSession()
    },
  },
}
</script>

<style scoped>
.booking-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.booking-engine {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(180deg, var(--bg-top), var(--bg-mid) 50%, var(--bg-bottom));
}

.booking-footer {
  background: linear-gradient(120deg, var(--hero-start), #0c305f);
  color: #fff;
}

.booking-footer .footer-brand {
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.booking-footer .footer-title {
  text-transform: uppercase;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
  color: #f5f8ff;
}

.booking-footer .footer-text {
  margin: 6px 0;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
}

.booking-footer .footer-link {
  color: #fff;
  text-decoration: underline;
}

.booking-footer .footer-bottom {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.footer-schedule {
  text-align: left !important;
}

.booking-app-bar {
  background: linear-gradient(90deg, var(--hero-start), var(--hero-end)) !important;
  box-shadow: 0 8px 20px rgba(13, 71, 161, 0.35);
}

.header-title {
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.header-subtitle {
  font-size: 0.85rem;
  opacity: 0.85;
  letter-spacing: 0.06em;
}

.header-contact .header-chip {
  border-color: rgba(255, 255, 255, 0.6) !important;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.hero-section {
  background: linear-gradient(120deg, var(--hero-start), var(--hero-end));
  color: #fff;
  position: relative;
  overflow: hidden;
}

.hero-section::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.2), transparent 50%);
  pointer-events: none;
}

.hero-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.25;
}

.hero-shape--one {
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.55), transparent 70%);
  top: -60px;
  left: -40px;
}

.hero-shape--two {
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent 60%);
  bottom: -90px;
  right: -60px;
}

.hero-kicker {
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-size: 0.75rem;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
}

.hero-title {
  font-size: 2.6rem;
  line-height: 1.2;
  margin-bottom: 12px;
}

.hero-subtitle {
  font-size: 1.05rem;
  max-width: 520px;
  color: rgba(255, 255, 255, 0.9);
}

.hero-chips .v-chip {
  border-color: rgba(255, 255, 255, 0.4) !important;
}

.hero-avatar {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-radius: 14px !important;
  background: rgba(25, 118, 210, 0.08);
  padding: 6px;
}

.summary-card {
  border-radius: 22px;
  background: linear-gradient(135deg, var(--card-start), var(--card-end));
  color: var(--text-primary);
  box-shadow: 0 22px 45px rgba(13, 71, 161, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.summary-card__body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.summary-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.summary-card__header--search {
  align-items: flex-start;
}

.summary-subtitle {
  display: block;
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 6px;
}

.summary-title {
  font-size: 1.25rem;
  font-weight: 700;
  display: block;
  line-height: 1.2;
}

.summary-card__title span {
  display: block;
}

.summary-eyebrow {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-muted);
  margin-bottom: 4px;
}

.summary-form :deep(.v-input__control) {
  background: #f7f9ff;
  border-radius: 999px;
}

.summary-input--active :deep(input),
.summary-input--active :deep(textarea) {
  color: #0d2b5b !important;
  font-weight: 600;
}

.summary-input--active :deep(.v-label),
.summary-input--active :deep(.v-label.v-label--active),
.summary-input--active :deep(.v-input--is-focused .v-label) {
  color: #0d2b5b !important;
}

.summary-input--active :deep(.v-text-field__prefix),
.summary-input--active :deep(.v-text-field__suffix) {
  color: #0d2b5b !important;
}

.summary-form :deep(.v-input__slot:before),
.summary-form :deep(.v-input__slot:after) {
  border-color: transparent !important;
}

.summary-form :deep(.v-icon) {
  color: var(--hero-end);
}

.summary-input {
  margin-bottom: 12px;
}

.search-action-btn {
  background: linear-gradient(135deg, var(--hero-start), var(--hero-end));
  color: #fff !important;
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  box-shadow: 0 12px 24px rgba(13, 71, 161, 0.25);
}

.search-action-btn::before {
  background-color: transparent !important;
}

.search-action-btn:hover {
  box-shadow: 0 16px 28px rgba(13, 71, 161, 0.32);
  transform: translateY(-1px);
}

.room-card__details-btn {
  color: var(--hero-start) !important;
  font-weight: 600;
  text-transform: none;
}

.room-card__swap-btn {
  border-radius: 10px;
  border: 1px dashed rgba(13, 71, 161, 0.35);
  text-transform: none;
  font-weight: 600;
}

.back-btn .v-btn__content {
  justify-content: center;
  width: 100%;
}

.action-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.action-row .v-btn {
  min-width: 140px;
}

.guest-extra-section {
  padding: 14px 16px;
  border: 1px solid rgba(15, 76, 129, 0.12);
  border-radius: 14px;
  background: rgba(247, 249, 255, 0.7);
}

.guest-extra-section__title {
  margin-bottom: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}

.summary-dates {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.summary-date {
  border-radius: 14px;
  border: 1px solid var(--outline-color);
  background: var(--bg-top);
  padding: 12px;
}

.summary-date--accent {
  background: linear-gradient(135deg, var(--hero-start), var(--hero-end));
  border-color: transparent;
  color: #fff;
}

.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--text-muted);
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.summary-date--accent .summary-label,
.summary-date--accent .summary-value {
  color: rgba(255, 255, 255, 0.9);
}

.summary-stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}

.summary-stat {
  display: flex;
  gap: 10px;
  border: 1px solid rgba(15, 76, 129, 0.08);
  border-radius: 16px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.7);
}

.summary-stat__icon {
  color: var(--hero-start) !important;
}

.summary-total {
  border-radius: 20px;
  padding: 18px;
  background: linear-gradient(120deg, var(--hero-start), var(--hero-end));
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  box-shadow: 0 10px 25px rgba(14, 62, 128, 0.3);
}

.summary-total .summary-label,
.summary-total .summary-value {
  color: rgba(255, 255, 255, 0.9);
}

.summary-total__value {
  font-size: 1.35rem;
  font-weight: 700;
}

.summary-empty {
  border: 1px dashed var(--outline-color);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.5);
}

.summary-divider {
  opacity: 0.3;
}

.summary-cta {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.summary-cta__text {
  margin: 4px 0 0;
  color: var(--text-muted);
  font-size: 0.85rem;
}

.adjust-search-btn {
  background: linear-gradient(135deg, var(--hero-start), var(--hero-end));
  color: #fff !important;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 10px 22px;
  box-shadow: 0 12px 25px rgba(0, 78, 162, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.adjust-search-btn::before {
  background-color: transparent !important;
}

.adjust-search-btn .v-icon {
  color: #fff !important;
}

.adjust-search-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 30px rgba(0, 78, 162, 0.3);
}

.engine-container {
  margin-top: -40px;
  position: relative;
  z-index: 2;
  padding-left: 16px;
  padding-right: 16px;
}

.engine-background {
  position: absolute;
  inset: 0;
  margin: 0 16px;
  border-radius: 32px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(227, 242, 253, 0.92));
  box-shadow: 0 35px 70px rgba(13, 71, 161, 0.15);
}

.glass-card {
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.96);
}

.engine-card {
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(15, 76, 129, 0.25);
  position: relative;
  z-index: 2;
}

.policy-card {
  border-radius: 22px;
  border: 1px solid rgba(25, 118, 210, 0.1);
}

.policy-card__title {
  font-weight: 700;
  color: var(--text-primary);
}

.policy-rule {
  background: rgba(25, 118, 210, 0.05);
  border-radius: 16px;
  padding: 16px;
  min-height: 120px;
  border: 1px solid rgba(25, 118, 210, 0.08);
}

.policy-rule__title {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.policy-rule__description {
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.4;
}

.booking-stepper {
  background: transparent;
}

::v-deep .booking-stepper .v-stepper__header,
::v-deep .booking-stepper .v-stepper__items {
  background: transparent;
  border-bottom: 1px solid var(--outline-color);
}

::v-deep .booking-stepper .v-stepper__step {
  padding: 24px 16px;
  color: var(--text-muted);
}

::v-deep .booking-stepper .v-stepper__step--active .v-stepper__label {
  color: var(--hero-start);
  font-weight: 600;
}

::v-deep .booking-stepper .v-stepper__step .v-stepper__step__step {
  background: #cfe0ff;
  color: var(--hero-start);
}

::v-deep .booking-stepper .v-stepper__step--active .v-stepper__step__step {
  background: var(--hero-end);
  color: #fff;
}

.search-card {
  border-radius: 20px;
  background: rgba(238, 244, 255, 0.95);
  border: 1px solid rgba(25, 118, 210, 0.12);
}

.section-card {
  border-radius: 20px;
  border: 1px solid rgba(25, 118, 210, 0.08);
}

.sticky-summary {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
}

.summary-value {
  font-weight: 500;
  color: var(--text-primary);
  text-align: right;
}

.room-card {
  border-radius: 18px !important;
  border: 1px solid rgba(15, 76, 129, 0.08);
  background: #fff;
  overflow: hidden;
}

.room-card--list {
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.06);
}

.room-card__top {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  padding: 16px;
}

.room-card__media {
  display: flex;
  align-items: center;
  justify-content: center;
}

.room-card__image {
  border-radius: 12px;
  overflow: hidden;
}

.room-card__no-image {
  width: 190px;
  height: 140px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.room-card__content {
  display: flex;
  flex-direction: column;
}

.room-card__title {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.room-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  font-size: 0.9rem;
  color: var(--text-muted);
}

.room-card__stat {
  display: flex;
  align-items: center;
}

.room-card__amenities {
  margin-top: 12px;
}

.amenity-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-bottom: 6px;
}

.amenity-line__items {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.room-card__bottom {
  background: #fbfbfc;
}

.room-card__grid {
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 1.1fr 0.7fr;
  gap: 0;
}

.room-card__col {
  padding: 16px;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  min-height: 120px;
}

.room-card__col:last-child {
  border-right: none;
}

.room-card__col-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 10px;
}

.room-card__rate-name {
  font-weight: 600;
  margin-bottom: 8px;
}

.room-card__badges .v-chip {
  border-color: rgba(0, 0, 0, 0.12);
}

.room-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.room-card__conditions {
  margin-top: 8px;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.room-card__condition-line {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  gap: 6px;
}

.room-card__condition-amount {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  color: var(--success-color);
  background: rgba(46, 125, 50, 0.08);
  padding: 2px 8px;
  border-radius: 999px;
  width: fit-content;
}

.room-card__policy-tags {
  margin-top: 6px;
}

.guest-icons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  color: #2c3e50;
}

.room-card__price {
  font-weight: 700;
  color: var(--text-primary);
}

.room-card__price-total {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 4px;
}

.room-card__price-note {
  font-size: 0.75rem;
  color: #a0a7b3;
  margin-top: 4px;
}

.room-card__rooms {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
  text-align: center;
}

.room-card__action {
  width: 100%;
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
}

.room-card__col--action {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 960px) {
  .room-card__top {
    grid-template-columns: 1fr;
  }

  .room-card__media {
    justify-content: flex-start;
  }

  .room-card__grid {
    grid-template-columns: 1fr;
  }

  .room-card__col {
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .room-card__col:last-child {
    border-bottom: none;
  }
}

.room-card-skeleton {
  border-radius: 22px;
}

.transparent-chip {
  background-color: transparent !important;
}

@media (max-width: 960px) {
  .hero-title {
    font-size: 2.1rem;
  }

  .engine-container {
    margin-top: 0;
    padding-left: 0;
    padding-right: 0;
  }
}

/* Destaque do Card de Parcelas */
.v-card.blue.lighten-5 {
  border: 2px solid #1976d2 !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
  animation: pulseGlow 2s ease-in-out;
}

@keyframes pulseGlow {
  0% {
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
  }
  50% {
    box-shadow: 0 6px 20px rgba(25, 118, 210, 0.35);
  }
  100% {
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
  }
}

/* Modo Embedded/Iframe - Layout Limpo */
.booking-app.embedded {
  min-height: auto;
}

.booking-engine.embedded {
  min-height: auto;
  padding-top: 0 !important;
}

/* Ajustes para hero section em modo embedded */
.booking-engine.embedded .hero-section {
  padding-top: 40px;
  min-height: auto;
}

/* Remover espaçamento extra quando não há header */
.booking-engine.embedded .main-content {
  margin-top: 0;
}

.cookie-consent {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 1200;
  background: #ffffff;
  border: 1px solid rgba(31, 42, 55, 0.15);
  box-shadow: 0 10px 24px rgba(19, 37, 69, 0.2);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cookie-consent__content {
  min-width: 0;
}

.cookie-consent__title {
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.cookie-consent__text {
  color: var(--text-muted);
  font-size: 0.92rem;
}

.cookie-consent__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

@media (max-width: 760px) {
  .cookie-consent {
    left: 10px;
    right: 10px;
    bottom: 10px;
    padding: 12px;
    flex-direction: column;
    align-items: flex-start;
  }

  .cookie-consent__actions {
    width: 100%;
  }
}
</style>
